/* ===================================================================
   SH Video Facade — V1 (2026-05-24)
   ===================================================================
   Powers <video data-sh-vid> + <button data-sh-yt-facade> +
   <button data-sh-vimeo-facade> emitted by snippets/sh-video.liquid

   Features:
     - IntersectionObserver autoplay for native <video data-sh-vid>
     - Click-to-load for YouTube/Vimeo facades (saves ~1 MB JS per embed)
     - Auto-promote facade → real iframe when in-view AND data-sh-vid-autoplay="1"
     - Network-aware: skip autoplay on saveData / 2g
     - MutationObserver: handles AJAX-inserted videos (popups, section reload)
     - Singleton — one init per page
   =================================================================== */
(function () {
  'use strict';
  if (window.__shVideoFacade) return;
  window.__shVideoFacade = true;

  var nc = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var skipAutoplay = !!(nc && (nc.saveData === true || /^(slow-)?2g$/.test(nc.effectiveType || '')));
  var IO_THRESHOLD = 0.25;

  /* ──────────────── NATIVE <video> ──────────────── */
  function setupNative(v) {
    if (v.__shInit) return;
    v.__shInit = true;
    if (v.getAttribute('data-sh-vid-autoplay') !== '1') return;
    var play = function () {
      if (skipAutoplay) return;
      try {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      } catch (e) {}
    };
    var pause = function () { try { v.pause(); } catch (e) {} };
    if (!('IntersectionObserver' in window)) { play(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && e.intersectionRatio >= IO_THRESHOLD) play();
        else pause();
      });
    }, { threshold: [0, IO_THRESHOLD, 0.5] });
    io.observe(v);
  }

  /* ──────────────── YOUTUBE FACADE ──────────────── */
  function promoteYT(btn) {
    if (btn.__shPromoted) return;
    btn.__shPromoted = true;
    var ytId = btn.getAttribute('data-yt-id');
    if (!ytId) return;
    var params = btn.getAttribute('data-yt-params') || '';
    var title = btn.getAttribute('data-yt-title') || 'YouTube video';
    var ifr = document.createElement('iframe');
    ifr.src = 'https://www.youtube-nocookie.com/embed/' + ytId + (params ? '?' + params : '');
    ifr.title = title;
    ifr.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    ifr.setAttribute('allowfullscreen', '');
    ifr.loading = 'lazy';
    ifr.className = 'sh-vid__iframe';
    /* Preserve aspect-ratio inline style if set on facade */
    var s = btn.getAttribute('style');
    if (s) ifr.setAttribute('style', s);
    /* Preserve class lineage so CSS rules still apply (excluding button-specific) */
    ifr.classList.add('sh-vid', 'sh-vid--youtube', 'sh-vid--promoted');
    btn.replaceWith(ifr);
  }

  /* ──────────────── VIMEO FACADE ──────────────── */
  function promoteVM(btn) {
    if (btn.__shPromoted) return;
    btn.__shPromoted = true;
    var vmId = btn.getAttribute('data-vm-id');
    if (!vmId) return;
    var params = btn.getAttribute('data-vm-params') || '';
    var title = btn.getAttribute('data-vm-title') || 'Vimeo video';
    var ifr = document.createElement('iframe');
    ifr.src = 'https://player.vimeo.com/video/' + vmId + (params ? '?' + params : '');
    ifr.title = title;
    ifr.allow = 'autoplay; fullscreen; picture-in-picture';
    ifr.setAttribute('allowfullscreen', '');
    ifr.loading = 'lazy';
    ifr.className = 'sh-vid__iframe';
    var s = btn.getAttribute('style');
    if (s) ifr.setAttribute('style', s);
    ifr.classList.add('sh-vid', 'sh-vid--vimeo', 'sh-vid--promoted');
    btn.replaceWith(ifr);
  }

  function setupFacade(btn, promote) {
    if (btn.__shInit) return;
    btn.__shInit = true;
    btn.addEventListener('click', function () { promote(btn); });
    /* Background / autoplay mode → auto-promote on IO */
    if (btn.getAttribute('data-sh-vid-autoplay') === '1' && !skipAutoplay && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.intersectionRatio >= IO_THRESHOLD) {
            promote(btn);
            io.disconnect();
          }
        });
      }, { threshold: IO_THRESHOLD });
      io.observe(btn);
    }
  }

  /* ──────────────── SCAN + OBSERVE ──────────────── */
  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('video[data-sh-vid]').forEach(setupNative);
    root.querySelectorAll('[data-sh-yt-facade]').forEach(function (b) { setupFacade(b, promoteYT); });
    root.querySelectorAll('[data-sh-vimeo-facade]').forEach(function (b) { setupFacade(b, promoteVM); });
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    scan(document);
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var nodes = muts[i].addedNodes;
          if (!nodes) continue;
          for (var j = 0; j < nodes.length; j++) {
            if (nodes[j].nodeType === 1) scan(nodes[j]);
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  });
})();
